import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, ArrowUp, Phone } from "lucide-react";
import { SITE } from "@/config/site";
import { XMark } from "./BrandMark";
import { sendChatMessage, CHAT_MESSAGE_MAX } from "@/lib/chatApi";
import { emitConversion } from "@/lib/conversionEvents";

const SESSION_KEY = "nexxsite-chat-session";
const HISTORY_KEY = "nexxsite-chat-history";
const SEEN_KEY = "nexxsite-chat-seen";
const WELCOME = "Cześć! Odpowiem na pytania o automatyzacje n8n, widoczność w AI i pozostałe usługi. W czym mogę pomóc?";
const SUGGESTIONS = ["Ile kosztuje chatbot?", "Czym jest GEO?", "Chcę wycenę projektu"];
const HIDDEN_ON = ["/admin", "/login"];

// sessionStorage bywa niedostępny (tryb prywatny, zablokowane dane witryny) — czat działa wtedy bez pamięci.
function readStorage(key) {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}
function writeStorage(key, value) {
  try { window.sessionStorage.setItem(key, value); } catch { /* brak pamięci sesji */ }
}

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const b = window.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// Jeden identyfikator na sesję karty — n8n trzyma po nim kontekst rozmowy.
function getSessionId() {
  let id = readStorage(SESSION_KEY);
  if (!id) { id = uuid(); writeStorage(SESSION_KEY, id); }
  return id;
}

function loadHistory() {
  try {
    const parsed = JSON.parse(readStorage(HISTORY_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((m) => m && typeof m.text === "string" && ["user", "assistant", "error"].includes(m.role)) : [];
  } catch { return []; }
}

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const openedOnce = useRef(false);
  // Puls przyciągający uwagę, dopóki czat nie zostanie otwarty w tej sesji.
  const [seen, setSeen] = useState(false);

  // Historia tylko po stronie klienta (prerender renderuje sam przycisk).
  useEffect(() => { setMessages(loadHistory()); setSeen(readStorage(SEEN_KEY) === "1"); }, []);
  useEffect(() => { if (messages.length) writeStorage(HISTORY_KEY, JSON.stringify(messages.slice(-60))); }, [messages]);

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus({ preventScroll: true });
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    // Na mobile panel jest pełnoekranowy — blokujemy przewijanie strony pod nim.
    const mobile = window.matchMedia("(max-width: 767px)").matches;
    const prevOverflow = document.body.style.overflow;
    if (mobile) document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [open]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, pending, open]);

  const send = useCallback(async (text) => {
    const message = text.trim().slice(0, CHAT_MESSAGE_MAX);
    if (!message || pending || blocked) return;
    setDraft("");
    setMessages((prev) => [...prev, { role: "user", text: message }]);
    setPending(true);
    emitConversion("chat_message_sent");
    try {
      const reply = await sendChatMessage({ message, sessionId: getSessionId(), sourcePage: window.location.pathname });
      setMessages((prev) => [...prev, { role: "assistant", text: reply }]);
    } catch (error) {
      if (error.status === 429) setBlocked(true);
      const text = error.limit === "session_limit" ? "W tej rozmowie wykorzystano limit 30 wiadomości."
        : error.status === 429 ? "Zbyt wiele wiadomości w ciągu godziny. Spróbuj później."
        : error.timeout ? "Odpowiedź trwa zbyt długo." : "Nie udało się teraz odpowiedzieć.";
      setMessages((prev) => [...prev, { role: "error", text }]);
    } finally {
      setPending(false);
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [pending, blocked]);

  const toggle = () => {
    setOpen((value) => !value);
    if (!openedOnce.current) { openedOnce.current = true; emitConversion("chat_open"); }
    if (!seen) { setSeen(true); writeStorage(SEEN_KEY, "1"); }
  };

  if (HIDDEN_ON.some((path) => pathname.startsWith(path))) return null;
  const hasUserMessages = messages.some((m) => m.role === "user");

  return (
    <>
      {open && (
        <>
          <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[69] hidden md:block">
            <div className="absolute bottom-20 right-3 h-[34rem] w-[28rem] rounded-[3rem] bg-[radial-gradient(closest-side,rgba(34,211,238,0.24),rgba(8,145,178,0.14)_62%,transparent)] blur-3xl" />
          </div>
          <section
            role="dialog"
            aria-label="Czat z asystentem NexXsite"
            data-testid="chat-panel"
            className="hero-dark fixed inset-0 z-[70] flex flex-col bg-[#0b0b10] text-zinc-100 md:inset-auto md:bottom-24 md:right-6 md:h-[min(620px,calc(100vh-8rem))] md:w-[390px] md:rounded-3xl md:border md:border-white/10 md:shadow-2xl"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
          >
            <header className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#09090b] ring-1 ring-white/10">
                  <XMark className="h-5 w-6 text-white" />
                </span>
                <div>
                  <div className="font-display text-base font-bold">Asystent NexXsite</div>
                  <div className="text-xs text-zinc-400">Odpowiada asystent AI</div>
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Zamknij czat" className="flex h-10 w-10 items-center justify-center rounded-full text-zinc-400 hover:bg-white/5 hover:text-zinc-100">
                <X className="h-5 w-5" />
              </button>
            </header>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
              <Bubble role="assistant">{WELCOME}</Bubble>
              {messages.map((m, i) => (
                m.role === "error" ? <ErrorBubble key={i} text={m.text} /> : <Bubble key={i} role={m.role}>{m.text}</Bubble>
              ))}
              {!hasUserMessages && !pending && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => send(s)} className="rounded-full border border-[var(--brand-accent)]/40 px-3.5 py-2 text-left text-sm text-zinc-200 hover:border-[var(--brand-accent)] hover:text-white">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {pending && (
                <div className="flex" aria-label="Asystent pisze">
                  <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-white/5 px-4 py-3">
                    {[0, 150, 300].map((delay) => (
                      <span key={delay} className="h-2 w-2 animate-bounce rounded-full bg-zinc-400" style={{ animationDelay: `${delay}ms` }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(draft); }}
              className="border-t border-white/10 px-4 pt-3"
              style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
            >
              <div className="flex items-end gap-2">
                <label htmlFor="chat-input" className="sr-only">Twoja wiadomość</label>
                <textarea
                  id="chat-input"
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  maxLength={CHAT_MESSAGE_MAX}
                  disabled={blocked}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(draft); } }}
                  placeholder={blocked ? "Limit wiadomości wykorzystany" : "Napisz wiadomość…"}
                  className="max-h-32 min-h-11 flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-base text-zinc-100 placeholder:text-zinc-500 outline-none focus:border-[var(--brand-accent)]/60 md:text-sm"
                />
                <button
                  type="submit"
                  disabled={!draft.trim() || pending || blocked}
                  aria-label="Wyślij"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[var(--brand-accent-from)] to-[var(--brand-accent-to)] text-zinc-950 disabled:opacity-40"
                >
                  <ArrowUp className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-[11px] leading-snug text-zinc-500">
                Rozmawiasz z asystentem AI. Podane dane kontaktowe przekazujemy naszemu zespołowi.{" "}
                <Link to="/polityka-prywatnosci#czat" onClick={() => setOpen(false)} className="underline hover:text-zinc-300">Polityka prywatności</Link>
              </p>
            </form>
          </section>
        </>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={open ? "Zamknij czat" : "Otwórz czat z asystentem"}
        data-testid="chat-toggle"
        className={`fixed right-4 z-[45] h-14 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[var(--brand-accent-from)] to-[var(--brand-accent-to)] text-zinc-950 shadow-xl shadow-cyan-500/40 ring-2 ring-white/40 transition-transform hover:scale-105 md:right-6 md:bottom-6 md:flex ${open ? "hidden w-14" : "flex w-14 md:w-auto md:px-5"} bottom-[calc(84px+env(safe-area-inset-bottom))]`}
      >
        {!open && !seen && (
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-cyan-400/50 motion-safe:animate-ping" />
        )}
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6 shrink-0" />}
        {!open && <span className="hidden text-sm font-semibold md:inline">Zapytaj asystenta AI</span>}
      </button>
    </>
  );
}

function Bubble({ role, children }) {
  const mine = role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : ""}`}>
      <div className={`max-w-[85%] whitespace-pre-wrap break-words px-4 py-2.5 text-sm leading-relaxed ${mine ? "rounded-2xl rounded-br-md bg-gradient-to-r from-[var(--brand-accent-from)] to-[var(--brand-accent-to)] text-zinc-950" : "rounded-2xl rounded-bl-md bg-white/5 text-zinc-200"}`}>
        {children}
      </div>
    </div>
  );
}

function ErrorBubble({ text }) {
  return (
    <div className="flex" role="alert">
      <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm leading-relaxed text-zinc-200">
        <p>{text}</p>
        <p className="mt-2">
          Zadzwoń:{" "}
          <a href={SITE.phoneHref} className="inline-flex items-center gap-1 font-semibold text-white underline"><Phone className="h-3.5 w-3.5" />{SITE.phone}</a>
          {" "}albo{" "}
          <Link to="/kontakt#kontakt" className="font-semibold text-white underline">napisz do nas</Link>.
        </p>
      </div>
    </div>
  );
}
